import type { CartaoFila, Fila as FilaTipo } from '@contefy/shared';
import {
  ESTADO_NOME,
  ESTADOS,
  PRIORIDADE_INFO,
  PRIORIDADES,
  TIPO_DEMANDA_INFO,
  TIPOS_DEMANDA,
} from '@contefy/shared';
import { LayoutGrid, List, Plus, RefreshCw } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router';
import { Alerta, Botao, Spinner } from '../componentes/ui';
import { equipeApi } from './api';
import { Bandeira, EtapaSelo, SinalSituacao, TipoDemandaSelo } from './componentes';
import { useUsuario } from './Layout';
import { NovaDemanda } from './NovaDemanda';

/** Tela inicial: uma coluna por etapa, cartões com sinal de cor. */
export function Fila() {
  const usuario = useUsuario();
  const [fila, setFila] = useState<FilaTipo | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [estado, setEstado] = useState('');
  const [tipo, setTipo] = useState('');
  const [prioridade, setPrioridade] = useState('');
  const [responsavel, setResponsavel] = useState('');
  const [soMinhas, setSoMinhas] = useState(false);
  const [visao, setVisao] = useState<'quadro' | 'lista'>('quadro');
  const [atualizando, setAtualizando] = useState(false);
  const [novaDemanda, setNovaDemanda] = useState(false);

  const carregar = useCallback(async () => {
    setAtualizando(true);
    try {
      setFila(await equipeApi.fila({ estado, responsavel, so_minhas: soMinhas, tipo, prioridade }));
      setErro(null);
    } catch (e) {
      setErro((e as Error).message);
    } finally {
      setAtualizando(false);
    }
  }, [estado, responsavel, soMinhas, tipo, prioridade]);

  useEffect(() => {
    carregar();
    const t = setInterval(carregar, 60_000);
    return () => clearInterval(t);
  }, [carregar]);

  if (erro) return <Alerta tipo="erro">{erro}</Alerta>;
  if (!fila)
    return (
      <div className="flex justify-center py-20 text-slate-500">
        <Spinner className="h-5 w-5" />
      </div>
    );

  // Todas as colunas, sempre: o quadro é o fluxo real do Societário, da triagem à conclusão.
  const colunas = fila.colunas;
  const todos = fila.colunas
    .flatMap((c) => c.cartoes)
    .sort(
      (a, b) =>
        (a.situacao === 'travada' ? -1 : 1) - (b.situacao === 'travada' ? -1 : 1) ||
        b.dias_na_etapa - a.dias_na_etapa,
    );

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Demandas do Societário</h1>
          <p className="text-sm text-slate-600">
            Cada demanda na etapa em que está. Vermelho precisa de alguém da equipe; a bandeira é a
            prioridade.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Botao className="!min-h-9 !py-1.5" onClick={() => setNovaDemanda(true)}>
            <Plus className="h-4 w-4" /> Nova demanda
          </Botao>
          <select
            value={tipo}
            onChange={(e) => setTipo(e.target.value)}
            className="!w-auto !py-1.5"
          >
            <option value="">Todos os tipos</option>
            {TIPOS_DEMANDA.map((t) => (
              <option key={t} value={t}>
                {TIPO_DEMANDA_INFO[t].titulo}
              </option>
            ))}
          </select>
          <select
            value={prioridade}
            onChange={(e) => setPrioridade(e.target.value)}
            className="!w-auto !py-1.5"
          >
            <option value="">Todas as prioridades</option>
            {PRIORIDADES.map((p) => (
              <option key={p} value={p}>
                {PRIORIDADE_INFO[p].titulo}
              </option>
            ))}
          </select>
          <select
            value={estado}
            onChange={(e) => setEstado(e.target.value)}
            className="!w-auto !py-1.5"
          >
            <option value="">Todos os estados</option>
            {ESTADOS.map((uf) => (
              <option key={uf} value={uf}>
                {ESTADO_NOME[uf]}
              </option>
            ))}
          </select>
          <select
            value={responsavel}
            onChange={(e) => setResponsavel(e.target.value)}
            className="!w-auto !py-1.5"
          >
            <option value="">Todos os responsáveis</option>
            {fila.responsaveis.map((r) => (
              <option key={r} value={r}>
                {r}
              </option>
            ))}
          </select>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={soMinhas}
              onChange={(e) => setSoMinhas(e.target.checked)}
            />
            Só o que precisa de mim{usuario ? '' : ''}
          </label>
          <div className="flex rounded-lg border border-slate-300 bg-white p-0.5">
            <button
              type="button"
              title="Quadro"
              onClick={() => setVisao('quadro')}
              className={`rounded-md p-1.5 ${visao === 'quadro' ? 'bg-slate-900 text-white' : 'text-slate-600'}`}
            >
              <LayoutGrid className="h-4 w-4" />
            </button>
            <button
              type="button"
              title="Lista"
              onClick={() => setVisao('lista')}
              className={`rounded-md p-1.5 ${visao === 'lista' ? 'bg-slate-900 text-white' : 'text-slate-600'}`}
            >
              <List className="h-4 w-4" />
            </button>
          </div>
          <Botao
            variante="secundario"
            className="!min-h-9 !py-1.5"
            carregando={atualizando}
            onClick={carregar}
          >
            <RefreshCw className="h-4 w-4" />
          </Botao>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-5">
        <Numero rotulo="Em andamento" valor={fila.totais.em_andamento} />
        <Numero
          rotulo="Esperando cliente"
          valor={fila.totais.aguardando_cliente}
          cor="text-amber-700"
        />
        <Numero
          rotulo="Esperando Junta"
          valor={fila.totais.aguardando_junta}
          cor="text-amber-700"
        />
        <Numero rotulo="Precisam de alguém" valor={fila.totais.travadas} cor="text-red-700" />
        <Numero rotulo="Concluídas" valor={fila.totais.concluidas} cor="text-emerald-700" />
      </div>

      {novaDemanda && <NovaDemanda aoFechar={() => setNovaDemanda(false)} />}

      {visao === 'quadro' ? (
        <Quadro colunas={colunas} />
      ) : (
        <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-4 py-2">Empresa</th>
                <th className="px-4 py-2">Etapa</th>
                <th className="px-4 py-2">Situação</th>
                <th className="px-4 py-2">Dias</th>
                <th className="px-4 py-2">Responsável</th>
              </tr>
            </thead>
            <tbody>
              {todos.map((c) => (
                <tr key={c.id} className="border-t border-slate-100 hover:bg-slate-50">
                  <td className="px-4 py-2">
                    <Link
                      to={`/equipe/aberturas/${c.id}`}
                      className="font-medium text-slate-900 hover:underline"
                    >
                      {c.nome_empresa ?? c.nome_contato}
                    </Link>
                    <div className="flex flex-wrap items-center gap-1.5 text-xs text-slate-500">
                      <Bandeira prioridade={c.prioridade} />
                      <TipoDemandaSelo tipo={c.tipo_demanda} />
                      {c.estado} · {c.tipo_empresa} · {c.nome_contato}
                    </div>
                  </td>
                  <td className="px-4 py-2">
                    <EtapaSelo etapa={c.etapa} />
                  </td>
                  <td className="px-4 py-2">
                    <SinalSituacao situacao={c.situacao} motivo={c.motivo} />
                    {c.motivo && <div className="text-xs text-slate-500">{c.motivo}</div>}
                  </td>
                  <td className="px-4 py-2 tabular-nums">{c.dias_na_etapa}</td>
                  <td className="px-4 py-2">
                    {c.responsavel ?? <span className="text-slate-400">—</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function Numero({
  rotulo,
  valor,
  cor = 'text-slate-900',
}: {
  rotulo: string;
  valor: number;
  cor?: string;
}) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white px-4 py-3">
      <div className={`text-2xl font-semibold tabular-nums ${cor}`}>{valor}</div>
      <div className="text-xs text-slate-500">{rotulo}</div>
    </div>
  );
}

function Cartao({ c }: { c: CartaoFila }) {
  const borda =
    c.situacao === 'travada'
      ? 'border-l-red-500'
      : c.situacao === 'andando'
        ? 'border-l-emerald-500'
        : 'border-l-amber-400';
  return (
    <Link
      to={`/equipe/aberturas/${c.id}`}
      className={`block rounded-xl border border-slate-200 border-l-4 bg-white p-3.5 shadow-sm transition hover:shadow ${borda}`}
    >
      <div className="flex items-start justify-between gap-2">
        <p className="min-w-0 break-words text-base font-semibold leading-snug text-slate-900">
          {c.nome_empresa ?? c.nome_contato}
        </p>
        <div className="flex shrink-0 items-center gap-1.5">
          <Bandeira prioridade={c.prioridade} />
          <span className="rounded bg-slate-100 px-1.5 text-[11px] font-semibold text-slate-600">
            {c.estado}
          </span>
        </div>
      </div>
      <div className="mt-1 flex flex-wrap items-center gap-1.5 text-xs text-slate-500">
        <TipoDemandaSelo tipo={c.tipo_demanda} />
        <span className="truncate">
          {c.tipo_empresa} · {c.nome_contato}
        </span>
      </div>
      {c.motivo && (
        <p
          className={`mt-2 text-sm ${c.situacao === 'travada' ? 'text-red-700' : 'text-slate-600'}`}
        >
          {c.motivo}
        </p>
      )}
      {c.observacoes.length > 0 && (
        <ul className="mt-2 space-y-1 border-t border-slate-100 pt-2">
          {c.observacoes.map((o) => (
            <li key={o.quando} className="text-xs text-slate-700">
              <span className="text-slate-400">
                {new Date(o.quando).toLocaleDateString('pt-BR')} · {o.quem}:
              </span>{' '}
              <span className="line-clamp-3">{o.texto}</span>
            </li>
          ))}
        </ul>
      )}
      <div className="mt-2 flex items-center justify-between text-[11px] text-slate-500">
        <span>
          {c.dias_na_etapa === 0
            ? 'hoje'
            : `${c.dias_na_etapa} dia${c.dias_na_etapa === 1 ? '' : 's'} na etapa`}
        </span>
        <span>{c.responsavel ?? 'sem responsável'}</span>
      </div>
    </Link>
  );
}

type Coluna = FilaTipo['colunas'][number];

/**
 * Quadro por etapa, do jeito clássico: a página rola na vertical e as colunas ficam inteiras.
 * A barra de rolagem horizontal nativa do quadro fica escondida e é substituída por uma barra
 * fixa no rodapé da tela, para não precisar descer a página até o fim para rolar de lado.
 */
function Quadro({ colunas }: { colunas: Coluna[] }) {
  const quadroRef = useRef<HTMLDivElement>(null);
  const barraRef = useRef<HTMLDivElement>(null);
  const [largura, setLargura] = useState(0);

  useEffect(() => {
    const quadro = quadroRef.current;
    if (!quadro) return;
    const medir = () => setLargura(quadro.scrollWidth);
    medir();
    const ro = new ResizeObserver(medir);
    ro.observe(quadro);
    for (const filho of Array.from(quadro.children)) ro.observe(filho);
    return () => ro.disconnect();
  }, [colunas]);

  // Mantém as duas barras na mesma posição, em qualquer direção que o usuário rolar.
  const sincronizar = (origem: HTMLDivElement | null, destino: HTMLDivElement | null) => {
    if (!origem || !destino || destino.scrollLeft === origem.scrollLeft) return;
    destino.scrollLeft = origem.scrollLeft;
  };

  const precisaBarra = largura > (quadroRef.current?.clientWidth ?? 0) + 1;

  return (
    <div>
      <div
        ref={quadroRef}
        onScroll={() => sincronizar(quadroRef.current, barraRef.current)}
        className="-mx-4 overflow-x-auto px-4 pb-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        <div className="flex min-w-max items-start gap-3">
          {colunas.map((col) => (
            <div key={col.etapa} className="w-80 shrink-0 rounded-2xl bg-slate-100/80 p-2">
              <div className="flex items-center justify-between px-2 py-1.5">
                <h2 className="text-sm font-semibold text-slate-800">{col.titulo}</h2>
                <span className="rounded-full bg-white px-2 text-xs font-medium text-slate-600">
                  {col.cartoes.length}
                </span>
              </div>
              <div className="space-y-2">
                {col.cartoes.map((c) => (
                  <Cartao key={c.id} c={c} />
                ))}
                {col.cartoes.length === 0 && (
                  <p className="px-2 py-6 text-center text-xs text-slate-400">Nenhuma</p>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
      {precisaBarra && (
        <div
          ref={barraRef}
          onScroll={() => sincronizar(barraRef.current, quadroRef.current)}
          title="Rolagem horizontal do quadro"
          className="sticky bottom-0 z-10 -mx-4 overflow-x-auto border-t border-slate-200 bg-slate-50/95 px-4 py-1 backdrop-blur [scrollbar-color:#64748b_#e2e8f0]"
        >
          <div style={{ width: largura }} className="h-2.5" />
        </div>
      )}
    </div>
  );
}
